/**
 * sessionStorage keys for the Automations module's "where you left off" view state — which card's
 * Customize panel was open, and any UNSAVED preference edits sitting inside it — scoped by the
 * module pane's `moduleId` (mirrors `modules/kanban/activeBoardStorage.ts` and
 * `modules/email/constants.ts`: the chat module pane unmounts the WHOLE module on switch-away, so
 * plain component state loses both the open panel and any in-progress edit on every pane switch,
 * #5570/#5571).
 */
import {
  readSessionPersisted,
  writeSessionPersisted,
} from "@/common/hooks/useSessionPersistedState";
import type { AutomationPreferences } from "@xavior/schema";
import { PRESET_ORDER } from "./preset-meta";
import type { PresetKind } from "./redesign/fixtures";

const PRESET_KINDS: ReadonlySet<string> = new Set(PRESET_ORDER);

/** Guards a stale/corrupt persisted value against the current preset kinds. */
export function isPresetKindOrNull(value: unknown): value is PresetKind | null {
  return value === null || (typeof value === "string" && PRESET_KINDS.has(value));
}

/** sessionStorage key for which card's Customize panel is expanded, per module instance. */
export function automationsOpenPanelKey(moduleId: string): string {
  return `xavior:automations.openPanel:${moduleId}`;
}

/** sessionStorage key for one automation's unsaved Customize-panel preference draft. */
export function automationPrefsDraftKey(moduleId: string, kind: PresetKind): string {
  return `xavior:automations.draftPrefs:${moduleId}:${kind}`;
}

function isDraftShape(value: unknown): value is AutomationPreferences | null {
  return value === null || (typeof value === "object" && !Array.isArray(value));
}

/** Read a stashed, unsaved preference draft — null when absent/invalid/disabled (no moduleId yet). */
export function readAutomationPrefsDraft(
  draftKey: string | null,
): AutomationPreferences | null {
  return readSessionPersisted<AutomationPreferences | null>(draftKey, null, isDraftShape);
}

/** Write-back the in-progress edit so a module-pane switch (full unmount) can't silently drop it. */
export function writeAutomationPrefsDraft(
  draftKey: string | null,
  prefs: AutomationPreferences,
): void {
  writeSessionPersisted(draftKey, prefs);
}

/** Best-effort removal — called once a save actually succeeds (no more unsaved diff to protect). */
export function clearAutomationPrefsDraft(draftKey: string | null): void {
  if (!draftKey || typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(draftKey);
  } catch {
    /* best effort */
  }
}
